import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import BillInvoicePreview from '../components/BillInvoicePreview';
import BillViewModal from '../components/BillViewModal';
import InvoicePreviewModal from '../components/InvoicePreviewModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface Patient {
  id: number;
  patient_name: string;
  patient_salutation?: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  case_start_date: string;
  address: string;
  case_manager?: string;
  patient_clients: { salutation?: string; client_name: string; contact_number_1: string; email: string }[];
}

type GroupKey = 'nursing' | 'nursing_visit' | 'doctor_visit' | 'physio' | 'medical_equipment' | 'equipment_rental' | 'equipment_sale' | 'misc';

interface LineItem {
  id?: number;
  particulars: string;
  rate: number | '';
  details: string;
  start_date: string;
  end_date: string;
  shift_type: string;
  shifts_count: number | '';
  total_amount: number;
  is_miscellaneous: boolean;
  has_gst: boolean;
  gst_percent: number;
  gst_amount: number;
  group: GroupKey;
  purchase_cost: number | '';
  mrp: number | '';
  discount_percent: number;
  disc_price: number | '';
  quantity: number | '';
  cgst_amount: number;
  sgst_amount: number;
  /** Medical equipment only. Pre-filled from the device's registration when the
   *  equipment is picked, then editable — it is saved on the line item, so a
   *  bill keeps the code it was raised under. */
  hsn_code: string;
  /** Medical equipment only. The manufacturer, pre-filled from the device's
   *  registration when the equipment is picked. Internal reference for the
   *  biller — it is deliberately never printed on the invoice. */
  company: string;
}

interface MedicalDeviceOption {
  item_name: string;
  company: string | null;
  hsn_code: string | null;
  purchase_cost: number | null;
  monthly_rental_rate: number | null;
  daily_rental_rate: number | null;
  selling_price: number | null;
  gst_applicable: string | null;
}

const STATUS_OPTIONS = ['Pending', 'Collected', 'Processed'];
const VISIT_OPTIONS = ['Morning Visit', 'Afternoon Visit', 'Evening Visit'];

const SHIFT_OPTIONS = [
  '08 Morning', '08 Afternoon', '08 Night',
  '12 Day', '12 Night', '24 Hrs',
  '08 Morning - 2', '08 Afternoon - 2', '08 Night - 2',
  '12 Day - 2', '12 Night - 2', '24 Hrs - 2',
  '08 Morning - Junior', '08 Afternoon - Junior', '08 Night - Junior',
  '12 Day - Junior', '12 Night - Junior', '24 Hrs Junior',
];

const SECTIONS = [
  { key: 'nursing'           as GroupKey, label: 'Nursing Billing',   color: 'blue',   headerBg: '#eff6ff', borderColor: '#bfdbfe', textColor: '#1d4ed8' },
  { key: 'nursing_visit'     as GroupKey, label: 'Nursing Visit',     color: 'purple', headerBg: '#f5f3ff', borderColor: '#ddd6fe', textColor: '#7c3aed' },
  { key: 'doctor_visit'      as GroupKey, label: 'Doctor Visit',      color: 'teal',   headerBg: '#f0fdfa', borderColor: '#99f6e4', textColor: '#0f766e' },
  { key: 'physio'            as GroupKey, label: 'Physiotherapy',     color: 'green',  headerBg: '#f0fdf4', borderColor: '#bbf7d0', textColor: '#15803d' },
  { key: 'medical_equipment' as GroupKey, label: 'Medical Equipment', color: 'violet', headerBg: '#faf5ff', borderColor: '#e9d5ff', textColor: '#7e22ce' },
  { key: 'equipment_rental'  as GroupKey, label: 'Equipment Rental',  color: 'violet', headerBg: '#faf5ff', borderColor: '#e9d5ff', textColor: '#7e22ce' },
  { key: 'equipment_sale'    as GroupKey, label: 'Equipment Sale',    color: 'indigo', headerBg: '#eef2ff', borderColor: '#c7d2fe', textColor: '#4338ca' },
  { key: 'misc'              as GroupKey, label: 'Miscellaneous',     color: 'amber',  headerBg: '#fffbeb', borderColor: '#fde68a', textColor: '#92400e' },
];

const SECTION_LABEL_MAP: Record<GroupKey, string> = {
  nursing: 'Nursing Billing',
  nursing_visit: 'Nursing Visit',
  doctor_visit: 'Doctor Visit',
  physio: 'Physiotherapy',
  medical_equipment: 'Medical Equipment',
  equipment_rental: 'Equipment Rental',
  equipment_sale: 'Equipment Sale',
  misc: 'Miscellaneous',
};

// Bill types that print their own "LHHS-ME-001-26-27" series (stored whole in
// invoice_no) instead of drawing a number from the shared bill_no counter.
const ME_BILL_TYPES: readonly string[] = ['medical_equipment', 'equipment_rental', 'equipment_sale'];

// The Company column next to Equipment Name in the equipment tables. It is a
// biller-side reference only and is never rendered on the invoice/PDF, which is
// why it lives nowhere in BillInvoicePreview. The client asked for it to be
// built but kept out of sight for now — flip this to true to show it; the value
// is captured from the device's registration and saved on the line item either
// way, so switching it on later reveals the data already on file.
const SHOW_EQUIPMENT_COMPANY_COLUMN = false;

// Equipment-sale fields that feed the retail math (price, discount, qty, GST).
// Editing anything else on a sale row — the HSN code, say — must leave the
// computed disc_price/CGST/SGST/total exactly as they are.
const MONEY_FIELDS: readonly string[] = [
  'mrp', 'discount_percent', 'disc_price', 'quantity', 'has_gst', 'gst_percent',
];

// ── Searchable dropdown shared by shift type & equipment ────────────────────
interface SearchDropdownProps {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  cellInputStyle: React.CSSProperties;
  placeholder?: string;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({ value, onChange, options, cellInputStyle, placeholder = 'Select...' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  // Which row the keyboard is on. -1 = nothing highlighted yet.
  const [active, setActive] = useState(-1);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setQuery(value); }, [value]);

  // The menu is portaled to <body> so the bills table's horizontal scroll can't
  // clip it — an absolutely positioned menu in a cell gets cut off near the
  // bottom of the table, or forces a scrollbar. Position tracks the input.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);   // capture: inner scrollers too
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => { if (!open) setActive(-1); }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
      onChange(query);            // free text is allowed — misc rows rely on it
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, query, onChange]);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));

  const commit = (val: string) => {
    onChange(val);
    setQuery(val);
    setOpen(false);
    setActive(-1);
  };

  // Arrow keys move the highlight, Enter takes it, Escape closes without
  // choosing — so a long device list is usable without the mouse.
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); onChange(query); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (filtered.length === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((prev) => (prev + step + filtered.length) % filtered.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && active >= 0 && filtered[active]) commit(filtered[active]);
      else { setOpen(false); onChange(query); }
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        ref={inputRef}
        style={{ ...cellInputStyle, paddingRight: '34px' }}
        value={query}
        placeholder={placeholder}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {/* Affordances the plain text box was missing: something to say this is a
          list, and a way to empty it without selecting all and deleting. */}
      <div style={{ position: 'absolute', right: '6px', display: 'flex', alignItems: 'center', gap: '2px', color: '#94a3b8', pointerEvents: 'none' }}>
        {query && (
          <span
            title="Clear"
            style={{ cursor: 'pointer', pointerEvents: 'auto', fontSize: '0.8rem', lineHeight: 1, padding: '0 2px' }}
            onMouseDown={(e) => { e.preventDefault(); commit(''); inputRef.current?.focus(); }}
          >✕</span>
        )}
        <span
          style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'inline-flex' }}
          onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); inputRef.current?.focus(); }}
        >
          <svg viewBox="0 0 10 10" width="9" height="9" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
            <polyline points="1,3 5,7 9,3" />
          </svg>
        </span>
      </div>

      {open && createPortal(
        <div
          ref={menuRef}
          data-portal-dropdown="true"
          style={{
            position: 'fixed', top: coords.top, left: coords.left,
            minWidth: Math.max(coords.width, 220), maxWidth: 380,
            backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
            boxShadow: '0 10px 24px rgba(15,23,42,0.14)', zIndex: 4000,
            maxHeight: '240px', overflowY: 'auto', padding: '3px',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '0.55rem 0.7rem', fontSize: '0.78rem', color: '#94a3b8' }}>
              {query ? `No match for "${query}" — it will be used as typed.` : 'No equipment registered yet.'}
            </div>
          ) : filtered.map((opt, i) => {
            const selected = opt === value;
            const highlighted = i === active;
            return (
              <div key={opt}
                style={{
                  padding: '0.42rem 0.6rem', cursor: 'pointer', fontSize: '0.82rem',
                  borderRadius: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  color: selected ? '#7e22ce' : '#334155',
                  fontWeight: selected ? 700 : 400,
                  backgroundColor: highlighted ? '#f1f5f9' : selected ? '#faf5ff' : 'white',
                }}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
              >
                {opt}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const today = new Date().toISOString().split('T')[0];

const fmtDateRange = (start: string, end: string) =>
  start && end ? `${start} to ${end}` : (start || end || '');

const BillingSheet: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId, billId } = useParams<{ patientId?: string; billId?: string }>();
  const isCreateMode = !!patientId && !billId;
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [createdBillId, setCreatedBillId] = useState<number | null>(null);   // open preview after creating

  const billType: GroupKey = (location.state as any)?.billType ?? 'nursing';

  const [patient, setPatient] = useState<Patient | null>(null);
  const [billNo, setBillNo] = useState<number | ''>('');
  // Medical-equipment bills carry a string series ("LHHS-ME-001-26-27") in place
  // of the numeric bill number. Display-only; the backend re-resolves it on save.
  const [invoiceNo, setInvoiceNo] = useState<string | null>(null);
  const [billDate, setBillDate] = useState(today);
  const [expectedPaymentDate, setExpectedPaymentDate] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [status, setStatus] = useState('Pending');
  const [remarks, setRemarks] = useState('');
  // One concession off the whole bill. Left blank, nothing is sent, nothing is
  // shown in the summary and nothing prints on the invoice.
  const [discountDescription, setDiscountDescription] = useState('');
  const [discountAmount, setDiscountAmount] = useState<number | ''>('');
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [deviceCatalog, setDeviceCatalog] = useState<string[]>([]);
  const [medicalDevices, setMedicalDevices] = useState<MedicalDeviceOption[]>([]);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // ── fetch device catalog + full medical device records ──────────────────
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/device-catalog/`, { headers })
      .then((r) => setDeviceCatalog(r.data.map((d: any) => d.item_name)))
      .catch(() => {});
    axios.get(`${API_BASE_URL}/api/medical-devices/`, { headers })
      .then((r) => setMedicalDevices(r.data))
      .catch(() => {});
  }, []);

  // ── fetch patient + bill data ───────────────────────────────────────────
  const fetchPatient = useCallback(async (id: string) => {
    const res = await axios.get(`${API_BASE_URL}/api/patients/${id}`, { headers });
    setPatient(res.data);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        if (isCreateMode && patientId) {
          await fetchPatient(patientId);
          if (ME_BILL_TYPES.includes(billType)) {
            // Medical-equipment bills use their own "LHHS-ME-001-26-27" series.
            const meRes = await axios.get(`${API_BASE_URL}/api/billing/next-medical-equipment-invoice-no`, { headers });
            setInvoiceNo(meRes.data.invoice_no);
          } else {
            const nbRes = await axios.get(`${API_BASE_URL}/api/billing/next-bill-no`, { headers });
            setBillNo(nbRes.data.bill_no);
          }
        } else if (billId) {
          const billRes = await axios.get(`${API_BASE_URL}/api/billing/bills/${billId}`, { headers });
          const b = billRes.data;
          await fetchPatient(String(b.patient_id));
          setBillNo(b.bill_no);
          setInvoiceNo(b.invoice_no || null);
          setBillDate(b.bill_date);
          setExpectedPaymentDate(b.expected_payment_date || '');
          setPeriodStart(b.invoice_period_start);
          setPeriodEnd(b.invoice_period_end);
          setStatus(b.status);
          setRemarks(b.remarks || '');
          setDiscountDescription(b.discount_description || '');
          setDiscountAmount(b.discount_amount == null ? '' : Number(b.discount_amount));

          const deriveGroup = (li: any): GroupKey => {
            if (li.section_type) return li.section_type as GroupKey;
            if (li.is_miscellaneous) return 'misc';
            const p = li.particulars || '';
            if (p === 'Nursing Billing') return 'nursing';
            if (p === 'Nursing Visit') return 'nursing_visit';
            if (p === 'Doctor Visit') return 'doctor_visit';
            if (p === 'Physiotherapy') return 'physio';
            if (p === 'Medical Equipment') return 'medical_equipment';
            return 'misc';
          };

          setLineItems(
            b.line_items.map((li: any) => {
              const group = deriveGroup(li);
              const rate = li.rate ?? '';
              const hasGst = li.has_gst ?? false;
              const gstPct = li.gst_percent ?? 18;
              const gstAmount = hasGst && rate !== '' ? Math.round(Number(rate) * (gstPct / 100) * 100) / 100 : 0;
              // Reopening a saved bill has to rebuild the CGST/SGST split too, or
              // the two columns read blank against a total that includes them.
              const halfGst = hasGst ? Math.round((gstAmount / 2) * 100) / 100 : 0;
              return {
                cgst_amount: li.cgst_amount ?? halfGst,
                sgst_amount: li.sgst_amount ?? halfGst,
                id: li.id,
                particulars: li.particulars,
                rate,
                details: li.details || '',
                start_date: li.item_start_date || '',
                end_date: li.item_end_date || '',
                shift_type: li.shift_type || '',
                shifts_count: li.shifts_count ?? '',
                total_amount: li.total_amount,
                is_miscellaneous: li.is_miscellaneous,
                has_gst: hasGst,
                gst_percent: gstPct,
                gst_amount: gstAmount,
                group,
                purchase_cost: '',
                hsn_code: li.hsn_code || '',
                company: li.company || '',
              };
            })
          );
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load data');
      }
    };
    load();
  }, [billId, patientId, isCreateMode]);

  // ── line item helpers ───────────────────────────────────────────────────
  const updateItem = (idx: number, field: keyof LineItem | 'has_gst', value: any) => {
    setLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const updated: LineItem = { ...item, [field]: value };
        if (!item.is_miscellaneous && (
              item.group === 'equipment_rental' ||
              (item.group === 'medical_equipment' && item.shift_type !== 'Sale')
            )) {
          if (item.group === 'equipment_rental') {
            // ── New CGST/SGST split math for dedicated rental flow ────────────
            if (field === 'rate' || field === 'has_gst' || field === 'gst_percent') {
              const r         = field === 'rate'     ? (parseFloat(value) || 0) : (parseFloat(String(item.rate)) || 0);
              const updGstPct = field === 'gst_percent' ? (Number(value) || 0)  : item.gst_percent;
              // There is no Yes/No toggle on rentals any more — the rate decides.
              // A rate of 0 means no GST, which is what a Non-GST device gives.
              const updHasGst = updGstPct > 0;
              updated.has_gst = updHasGst;
              if (updHasGst) {
                const half_gst = Math.round(r * (updGstPct / 2 / 100) * 100) / 100;
                updated.cgst_amount = half_gst;
                updated.sgst_amount = half_gst;
              } else {
                updated.cgst_amount = 0;
                updated.sgst_amount = 0;
              }
              updated.gst_amount   = updated.cgst_amount + updated.sgst_amount;
              updated.total_amount = Math.round((r + updated.gst_amount) * 100) / 100;
            }
          } else {
            // ── Legacy medical_equipment rental rows (backward compat) ────────
            if (field === 'rate' || field === 'has_gst' || field === 'gst_percent') {
              const r = field === 'rate' ? (parseFloat(value) || 0) : (parseFloat(String(item.rate)) || 0);
              const gst_on = field === 'has_gst' ? value : item.has_gst;
              const pct = (field === 'gst_percent' ? Number(value) : item.gst_percent) / 100;
              const gst_amount = gst_on ? Math.round(r * pct * 100) / 100 : 0;
              updated.gst_amount = gst_amount;
              updated.total_amount = Math.round((r + gst_amount) * 100) / 100;
            }
          }
        } else if (!item.is_miscellaneous && (
              item.group === 'equipment_sale' ||
              (item.group === 'medical_equipment' && item.shift_type === 'Sale')
            )) {
          if (item.group === 'equipment_sale') {
            // ── New retail math for dedicated sale flow ──────────────────────
            // Only the money fields re-run this. Editing something inert like the
            // HSN code must not, or disc_price would be recomputed from the
            // discount percent and silently overwrite a hand-typed price.
            if (!MONEY_FIELDS.includes(field as string)) return updated;
            const updHasGst  = field === 'has_gst'          ? (value as boolean)       : item.has_gst;
            const updMrp     = field === 'mrp'              ? (parseFloat(value) || 0) : (Number(item.mrp) || 0);
            const updDiscPct = field === 'discount_percent'  ? (Number(value) || 0)    : item.discount_percent;
            const updQty     = field === 'quantity'          ? (parseFloat(value) || 0) : (Number(item.quantity) || 0);
            const updGstPct  = field === 'gst_percent'       ? (Number(value) || 0)    : item.gst_percent;

            // when GST is toggled off, zero out the percent so the input clears
            if (field === 'has_gst' && !value) updated.gst_percent = 0;

            let discPrice: number;
            if (field === 'disc_price') {
              discPrice = parseFloat(value) || 0;
              updated.discount_percent = updMrp > 0 ? ((updMrp - discPrice) / updMrp) * 100 : 0;
            } else {
              discPrice = updMrp * (1 - updDiscPct / 100);
              updated.disc_price = discPrice;
            }
            const taxable = discPrice * updQty;
            if (updHasGst) {
              const half_gst = taxable * (updGstPct / 2 / 100);
              updated.cgst_amount = Math.round(half_gst * 100) / 100;
              updated.sgst_amount = Math.round(half_gst * 100) / 100;
            } else {
              updated.cgst_amount = 0;
              updated.sgst_amount = 0;
            }
            updated.gst_amount   = updated.cgst_amount + updated.sgst_amount;
            updated.total_amount = Math.round((taxable + updated.cgst_amount + updated.sgst_amount) * 100) / 100;
            if (field === 'mrp') updated.rate = parseFloat(value) || '';
          } else {
            // ── Legacy medical_equipment sale rows (backward compat) ──────────
            if (field === 'rate' || field === 'has_gst' || field === 'gst_percent') {
              const r = field === 'rate' ? (parseFloat(value) || 0) : (parseFloat(String(item.rate)) || 0);
              const gst_on = field === 'has_gst' ? value : item.has_gst;
              const pct = (field === 'gst_percent' ? Number(value) : item.gst_percent) / 100;
              const gst_amount = gst_on ? Math.round(r * pct * 100) / 100 : 0;
              updated.gst_amount = gst_amount;
              updated.total_amount = Math.round((r + gst_amount) * 100) / 100;
            }
          }
        } else if (item.group === 'doctor_visit' && !item.is_miscellaneous && field === 'rate') {
          updated.total_amount = parseFloat(value) || 0;
        } else if (!item.is_miscellaneous && (field === 'rate' || field === 'shifts_count')) {
          const r = field === 'rate' ? parseFloat(value) || 0 : parseFloat(String(item.rate)) || 0;
          const s = field === 'shifts_count' ? parseFloat(value) || 0 : parseFloat(String(item.shifts_count)) || 0;
          updated.total_amount = r * s;
        }
        return updated;
      })
    );
  };

  const emptyItem = (group: GroupKey, isMisc: boolean): LineItem => ({
    particulars: isMisc ? 'Miscellaneous' : (['medical_equipment', 'equipment_rental', 'equipment_sale'] as GroupKey[]).includes(group) ? '' : group === 'doctor_visit' ? 'Doctor consulting charges' : SECTION_LABEL_MAP[group],
    rate: '',
    details: '',
    start_date: '',
    end_date: '',
    shift_type: '',
    shifts_count: '',
    total_amount: 0,
    is_miscellaneous: isMisc,
    has_gst: false,
    gst_percent: 18,
    gst_amount: 0,
    group,
    purchase_cost: '',
    mrp: '',
    discount_percent: 0,
    disc_price: '',
    quantity: '',
    cgst_amount: 0,
    sgst_amount: 0,
    hsn_code: '',
    company: '',
  });

  const gstFromApplicable = (g: string | null | undefined): { has_gst: boolean; gst_percent: number } => {
    if (!g || g === 'Non-GST') return { has_gst: false, gst_percent: 18 };
    return { has_gst: true, gst_percent: parseInt(g) || 18 };
  };

  const addRentalRow = () => setLineItems((p) => [
    ...p,
    { ...emptyItem('equipment_rental', false), shift_type: 'Monthly', start_date: periodStart, end_date: periodEnd },
  ]);

  const addSaleRow = () => setLineItems((p) => [
    ...p,
    { ...emptyItem('equipment_sale', false), shift_type: 'Sale', quantity: 1, start_date: periodStart, end_date: periodEnd },
  ]);

  useEffect(() => {
    if (!isCreateMode) return;
    if (billType === 'equipment_rental') {
      setLineItems([{ ...emptyItem('equipment_rental', false), shift_type: 'Monthly', start_date: '', end_date: '' }]);
    } else if (billType === 'equipment_sale') {
      setLineItems([{ ...emptyItem('equipment_sale', false), shift_type: 'Sale', shifts_count: 1, start_date: '', end_date: '' }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectEquipmentForRental = (idx: number, name: string, _rateType: string) => {
    const dev = medicalDevices.find((d) => d.item_name === name);
    const has_gst = !!(dev?.gst_applicable && dev.gst_applicable !== 'Non-GST');
    const gst_percent = has_gst ? (parseInt(dev!.gst_applicable!) || 0) : 0;
    const rate = dev?.monthly_rental_rate ?? '';
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const r = parseFloat(String(rate)) || 0;
      const half_gst = has_gst ? Math.round(r * (gst_percent / 2 / 100) * 100) / 100 : 0;
      const cgst_amount = half_gst;
      const sgst_amount = half_gst;
      const gst_amount = cgst_amount + sgst_amount;
      const total_amount = Math.round((r + gst_amount) * 100) / 100;
      return { ...item, particulars: name, hsn_code: dev?.hsn_code || '', company: dev?.company || '', rate, has_gst, gst_percent, cgst_amount, sgst_amount, gst_amount, total_amount };
    }));
  };

  const selectEquipmentForSale = (idx: number, name: string) => {
    const dev = medicalDevices.find((d) => d.item_name === name);
    const has_gst = !!(dev?.gst_applicable && dev.gst_applicable !== 'Non-GST');
    const gst_percent = has_gst ? (parseInt(dev!.gst_applicable!) || 0) : 0;
    const mrp = dev?.selling_price ?? '';
    const purchase_cost = dev?.purchase_cost ?? '';
    const mrpNum = parseFloat(String(mrp)) || 0;
    const disc_price = mrpNum;
    const taxable = disc_price * 1;
    const half_gst = has_gst ? taxable * (gst_percent / 2 / 100) : 0;
    const cgst_amount = Math.round(half_gst * 100) / 100;
    const sgst_amount = Math.round(half_gst * 100) / 100;
    const total_amount = Math.round((taxable + cgst_amount + sgst_amount) * 100) / 100;
    setLineItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      return {
        ...item, particulars: name, hsn_code: dev?.hsn_code || '', company: dev?.company || '', purchase_cost, mrp, has_gst,
        discount_percent: 0, disc_price, quantity: 1,
        gst_percent, cgst_amount, sgst_amount,
        gst_amount: cgst_amount + sgst_amount,
        total_amount, rate: mrp,
      };
    }));
  };

  const addSectionRow = (group: GroupKey) => setLineItems((p) => [
    ...p,
    { ...emptyItem(group, false), start_date: periodStart, end_date: periodEnd },
  ]);
  const addMiscToSection = (group: GroupKey) => setLineItems((p) => [
    ...p,
    { ...emptyItem(group, true), start_date: periodStart, end_date: periodEnd },
  ]);
  const removeItem = (idx: number) => setLineItems((p) => p.filter((_, i) => i !== idx));

  // ── auto-populate (nursing only) ────────────────────────────────────────
  const handleAutoPopulate = async () => {
    if (!patient || !periodStart || !periodEnd) {
      toast.error('Set the invoice period before auto-populating');
      return;
    }
    setAutoPopulating(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/billing/patients/${patient.id}/auto-populate`,
        { params: { period_start: periodStart, period_end: periodEnd }, headers }
      );
      const items: LineItem[] = res.data.map((r: any) => ({
        ...emptyItem('nursing', false),
        particulars: r.particulars,
        rate: r.rate,
        details: r.details,
        shift_type: r.shift_type,
        shifts_count: r.shifts,
        total_amount: r.total,
        start_date: r.start_date || '',
        end_date: r.end_date || '',
      }));
      if (items.length === 0) {
        toast('No assignments found for this period', { icon: '⚠️' });
      } else {
        setLineItems(items);
        toast.success(`Populated ${items.length} line item(s)`);
      }
    } catch {
      toast.error('Auto-populate failed');
    } finally {
      setAutoPopulating(false);
    }
  };

  // ── save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const isMedEquipBill = ME_BILL_TYPES.includes(derivedPrimary);
    if (!isMedEquipBill && !billNo) { toast.error('Bill number is required'); return; }
    if (!billDate) { toast.error('Bill date is required'); return; }
    if (!periodStart || !periodEnd) { toast.error('Invoice period is required'); return; }
    if (lineItems.length === 0) { toast.error('Add at least one line item'); return; }

    const payload = {
      patient_id: patient?.id,
      bill_no: isMedEquipBill ? null : billNo,
      bill_date: billDate,
      expected_payment_date: expectedPaymentDate || null,
      invoice_period_start: periodStart,
      invoice_period_end: periodEnd,
      status,
      bill_type: derivedPrimary,
      remarks: remarks || null,
      // Null rather than 0 when unused, so a bill without a discount is stored
      // the same as one raised before the feature existed.
      discount_description: discountValue > 0 ? (discountDescription || null) : null,
      discount_amount: discountValue > 0 ? discountValue : null,
      line_items: lineItems.map((item, idx) => ({
        s_no: idx + 1,
        particulars: item.particulars,
        rate: item.rate === '' ? null : Number(item.rate),
        details: item.is_miscellaneous ? item.details : fmtDateRange(item.start_date, item.end_date),
        shift_type: item.shift_type || null,
        shifts_count: item.group === 'doctor_visit' ? 1 : (item.shifts_count === '' ? null : Number(item.shifts_count)),
        total_amount: item.total_amount,
        is_miscellaneous: item.is_miscellaneous,
        item_start_date: item.start_date || null,
        item_end_date: item.end_date || null,
        has_gst: item.has_gst,
        gst_percent: item.group === 'equipment_sale' ? item.gst_percent : (item.has_gst ? item.gst_percent : null),
        section_type: item.group,
        // Equipment only — every other type has no HSN to carry.
        hsn_code: ME_BILL_TYPES.includes(item.group) ? ((item.hsn_code || '').trim() || null) : null,
        // Likewise equipment only. Saved so the bill keeps the company it was
        // raised under; it is never rendered on the invoice.
        company: ME_BILL_TYPES.includes(item.group) ? ((item.company || '').trim() || null) : null,
        ...(item.group === 'equipment_sale' ? {
          mrp: item.mrp === '' ? null : Number(item.mrp),
          discount_percent: item.discount_percent,
          disc_price: item.disc_price === '' ? null : Number(item.disc_price),
          quantity: item.quantity === '' ? null : Number(item.quantity),
          cgst_amount: item.cgst_amount,
          sgst_amount: item.sgst_amount,
        } : {}),
      })),
    };

    setSaving(true);
    try {
      if (isCreateMode) {
        const res = await axios.post(`${API_BASE_URL}/api/billing/bills`, payload, { headers });
        toast.success('Bill created successfully');
        setShowPreview(false);           // don't leave the draft preview stacked under the saved one
        setCreatedBillId(res.data.id);   // open the preview so it can be printed/downloaded right away
      } else {
        await axios.patch(`${API_BASE_URL}/api/billing/bills/${billId}`, payload, { headers });
        toast.success('Bill saved successfully');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to save bill');
    } finally {
      setSaving(false);
    }
  };

  // ── PDF ──────────────────────────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) {
      toast.error('Preview not ready — please wait a moment');
      return;
    }
    setDownloadingPDF(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      const dataUrl = await toPng(invoiceRef.current, { pixelRatio: 2, backgroundColor: '#ffffff', cacheBust: true });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      pdf.addImage(dataUrl, 'PNG', 0, 0, 210, 297);
      const fmtD = (d: string) => d.split('-').reverse().join('-');
      const patientName = (patient?.patient_name || 'Patient').replace(/\s+/g, '_');
      const rentalLineItems = lineItems.filter((i) => !i.is_miscellaneous && i.group === 'equipment_rental');
      const displayStart = periodStart || rentalLineItems[0]?.start_date || '';
      const displayEnd   = periodEnd   || rentalLineItems[rentalLineItems.length - 1]?.end_date || '';
      const filename =
        derivedPrimary === 'equipment_sale'
          ? `MedicalEquipment_Billing_${patientName}_${invoiceNo || billNo}_${fmtD(billDate)}.pdf`
          : derivedPrimary === 'equipment_rental'
            ? `MERent_billing_${patientName}_${invoiceNo || billNo}_${fmtD(displayStart)}_to_${fmtD(displayEnd)}.pdf`
            : `Nursing_Billing_${patientName}_${billNo}_${fmtD(periodStart)} to ${fmtD(periodEnd)}.pdf`;
      pdf.save(filename);
    } catch (e) {
      console.error('PDF generation failed:', e);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // ── computed ─────────────────────────────────────────────────────────────
  const itemsTotal = lineItems.reduce((s, i) => s + i.total_amount, 0);
  // Clamped at the bill value — a discount bigger than the bill zeroes it
  // rather than turning the invoice into a credit note.
  const discountValue = Math.min(Math.max(Number(discountAmount) || 0, 0), itemsTotal);
  const grandTotal = itemsTotal - discountValue;
  const derivedPrimary: GroupKey = (() => {
    const first = lineItems.find((i) => !i.is_miscellaneous);
    return first ? first.group : billType;
  })();
  const visibleSections = SECTIONS.filter((sec) => sec.key === derivedPrimary || sec.key === 'misc');


  // ── styles ────────────────────────────────────────────────────────────────
  const s: Record<string, React.CSSProperties> = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
    backBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' },
    title: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: '3px 0 0', fontSize: '0.85rem', color: '#64748b' },
    actions: { display: 'flex', gap: '10px' },
    card: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '1.5rem' },
    sectionTitle: { margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' },
    grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
    fieldLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    input: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const },
    select: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' as const, backgroundColor: 'white' },
    patientInfo: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' },
    infoLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    infoValue: { fontSize: '0.88rem', color: '#334155', marginTop: '2px' },
    table: { width: '100%', borderCollapse: 'collapse' as const, marginBottom: '0.5rem' },
    th: { padding: '0.6rem 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.04em', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' as const },
    td: { padding: '0.5rem 0.5rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' as const },
    cellInput: { width: '100%', padding: '0.4rem 0.5rem', borderRadius: '5px', border: '1px solid #e2e8f0', fontSize: '0.82rem', boxSizing: 'border-box' as const },
    summaryBox: { backgroundColor: '#f8fafc', borderRadius: '8px', padding: '1rem', border: '1px solid #e2e8f0' },
    summaryLine: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem', color: '#334155' },
    grandTotalLine: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', borderTop: '2px solid #e2e8f0', marginTop: '4px' },
    deleteBtn: { padding: '0.3rem 0.5rem', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '0.8rem' },
    textarea: { width: '100%', padding: '0.55rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', resize: 'vertical' as const, minHeight: '80px', boxSizing: 'border-box' as const },
    autoPopBtn: { padding: '0.5rem 1rem', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 },
  };

  const btn = (variant: string): React.CSSProperties => ({
    padding: '0.55rem 1.2rem', borderRadius: '8px', cursor: 'pointer',
    fontSize: '0.88rem', fontWeight: 600,
    backgroundColor: variant === 'primary' ? '#2563eb' : variant === 'green' ? '#16a34a' : variant === 'outline' ? 'white' : '#f1f5f9',
    color: variant === 'primary' || variant === 'green' ? 'white' : '#334155',
    border: variant === 'outline' ? '1px solid #cbd5e1' : 'none',
  });

  const sectionAddBtn = (color: string): React.CSSProperties => {
    const map: Record<string, { border: string; bg: string; text: string }> = {
      blue:   { border: '#93c5fd', bg: '#eff6ff', text: '#1d4ed8' },
      purple: { border: '#c4b5fd', bg: '#f5f3ff', text: '#7c3aed' },
      teal:   { border: '#5eead4', bg: '#f0fdfa', text: '#0f766e' },
      green:  { border: '#86efac', bg: '#f0fdf4', text: '#15803d' },
      violet: { border: '#d8b4fe', bg: '#faf5ff', text: '#7e22ce' },
      indigo: { border: '#a5b4fc', bg: '#eef2ff', text: '#4338ca' },
      amber:  { border: '#fcd34d', bg: '#fffbeb', text: '#92400e' },
    };
    const c = map[color] || map.green;
    return {
      padding: '0.28rem 0.7rem', borderRadius: '5px', cursor: 'pointer',
      fontSize: '0.76rem', fontWeight: 500,
      border: `1px dashed ${c.border}`,
      backgroundColor: c.bg, color: c.text,
    };
  };

  const fmtAmt = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const client = patient?.patient_clients?.[0];

  // ── section renderers ────────────────────────────────────────────────────
  const renderSectionHeader = (sec: typeof SECTIONS[number]) => (
    <div style={{
      padding: '0.5rem 0.75rem', backgroundColor: sec.headerBg,
      borderTop: `2px solid ${sec.borderColor}`, borderBottom: `1px solid ${sec.borderColor}`,
      borderRadius: '6px 6px 0 0',
      fontWeight: 700, fontSize: '0.75rem', color: sec.textColor,
      textTransform: 'uppercase' as const, letterSpacing: '0.06em',
    }}>
      {sec.label}
    </div>
  );

  const renderSectionFooter = (sec: typeof SECTIONS[number]) => (
    <div style={{ display: 'flex', gap: '6px', padding: '0.5rem 0', borderBottom: `2px solid ${sec.borderColor}`, marginBottom: '1rem' }}>
      {sec.key !== 'misc' && (
        <button style={sectionAddBtn(sec.color)} onClick={() => addSectionRow(sec.key)}>
          + Add {sec.label}
        </button>
      )}
      <button style={sectionAddBtn('green')} onClick={() => addMiscToSection(sec.key)}>
        + Add Misc
      </button>
    </div>
  );

  const renderNoItems = (cols: number) => (
    <tr>
      <td colSpan={cols} style={{ padding: '0.5rem 0.75rem', color: '#cbd5e1', fontSize: '0.8rem', fontStyle: 'italic', textAlign: 'center' }}>
        No items — use the buttons below to add
      </td>
    </tr>
  );

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button style={s.backBtn} onClick={() => {
            const from = (location.state as any)?.from;
            if (from === 'deleted') navigate('/billing/deleted');
            else navigate(patient ? `/billing/patient/${patient.id}` : '/billing');
          }}>← Back</button>
          <h2 style={s.title}>
            {isCreateMode ? `New ${SECTIONS.find((sec) => sec.key === billType)?.label ?? ''} ` : `Bill #${billNo}`}
          </h2>
        </div>
        <div style={s.actions}>
          <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
          <button style={btn('primary')} onClick={handleSave} disabled={saving || createdBillId !== null}>
            {saving ? 'Saving...' : isCreateMode ? 'Create Bill' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Client info */}
      {patient && (
        <div style={s.card}>
          <p style={s.sectionTitle}>Client Details</p>
          <div style={s.patientInfo}>
            {[
              { label: 'Client', value: client?.client_name || '—' },
              { label: 'Contact', value: client?.contact_number_1 || '—' },
              { label: 'CID', value: `LHHS${patient.id}` },
              { label: 'Case Manager', value: patient.case_manager || '—' },
              { label: 'Patient', value: patient.patient_name },
              { label: 'Age / Sex', value: `${patient.age || '—'} / ${patient.sex || '—'}` },
              { label: 'Doctor', value: patient.treating_details?.[0]?.doctor || patient.treating_doctor || '—' },
              { label: 'Hospital', value: patient.treating_details?.[0]?.hospital || patient.treating_hospital || '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={s.infoLabel}>{label}</div>
                <div style={{ ...s.infoValue, fontWeight: label === 'Client' ? 700 : undefined }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bill details */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Bill Details</p>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'nowrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Bill Date</label>
            <input type="date" style={s.input} value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>{ME_BILL_TYPES.includes(derivedPrimary) ? 'Invoice Number' : 'Bill Number'}</label>
            {ME_BILL_TYPES.includes(derivedPrimary) ? (
              <input type="text" style={{ ...s.input, backgroundColor: '#f8fafc', color: '#475569' }}
                value={invoiceNo || 'Auto-generated on save'} readOnly />
            ) : (
              <input type="number" style={s.input} value={billNo}
                onChange={(e) => setBillNo(e.target.value === '' ? '' : Number(e.target.value))} placeholder="e.g. 12917" />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Bill Start Date</label>
            <input type="date" style={s.input} value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Bill End Date</label>
            <input type="date" style={s.input} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Status</label>
            <select style={s.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label style={s.fieldLabel}>Exp Payment Date</label>
            <input type="date" style={s.input} value={expectedPaymentDate} onChange={(e) => setExpectedPaymentDate(e.target.value)} />
          </div>
          {derivedPrimary === 'nursing' && (
            <div style={{ flexShrink: 0 }}>
              <button style={s.autoPopBtn} onClick={handleAutoPopulate} disabled={autoPopulating}>
                {autoPopulating ? 'Fetching...' : '⚡ Auto-populate from Assignments'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Line items — per-section tables */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Invoice Line Items</p>
        {(() => {
          let counter = 0;
          return visibleSections.map((sec) => {
            const sectionItems = lineItems.map((item, idx) => ({ item, idx })).filter(({ item }) => item.group === sec.key);

            if (sec.key === 'medical_equipment') {
              // ── Medical Equipment — Rental + Sale sub-tables ────────────
              const rentalRows = sectionItems.filter(({ item }) => !item.is_miscellaneous && item.shift_type !== 'Sale');
              const saleRows   = sectionItems.filter(({ item }) => !item.is_miscellaneous && item.shift_type === 'Sale');
              const miscEquipRows = sectionItems.filter(({ item }) => item.is_miscellaneous);
              const subHeader = (label: string, color: string, borderColor: string) => (
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase' as const, letterSpacing: '0.05em', padding: '0.35rem 0', borderBottom: `1px solid ${borderColor}`, marginBottom: '0.2rem', marginTop: '0.6rem' }}>{label}</div>
              );
              return (
                <div key={sec.key}>
                  {renderSectionHeader(sec)}

                  {/* ── Medical Equipment Rental Services ─────────────── */}
                  {subHeader('Medical Equipment Rental Services', '#7e22ce', '#e9d5ff')}
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, width: '3%' }}>#</th>
                        <th style={{ ...s.th, width: '21%' }}>Equipment Name</th>
                        {SHOW_EQUIPMENT_COMPANY_COLUMN && <th style={{ ...s.th, width: '12%' }}>Company</th>}
                        <th style={{ ...s.th, width: '10%' }}>Start Date</th>
                        <th style={{ ...s.th, width: '10%' }}>End Date</th>
                        <th style={{ ...s.th, width: '8%', textAlign: 'center' }}>HSN</th>
                        <th style={{ ...s.th, width: '6%' }}>Rate Type</th>
                        <th style={{ ...s.th, width: '9%' }}>Rate ₹</th>
                        <th style={{ ...s.th, width: '5%' }}>GST?</th>
                        <th style={{ ...s.th, width: '6%' }}>GST %</th>
                        <th style={{ ...s.th, width: '9%', textAlign: 'right' }}>GST Amt ₹</th>
                        <th style={{ ...s.th, width: '10%', textAlign: 'right' }}>Total ₹</th>
                        <th style={{ ...s.th, width: '3%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rentalRows.length === 0 ? renderNoItems(SHOW_EQUIPMENT_COMPANY_COLUMN ? 13 : 12) : rentalRows.map(({ item, idx }) => {
                        counter++;
                        return (
                          <tr key={idx}>
                            <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{counter}</td>
                            <td style={s.td}>
                              <SearchDropdown value={item.particulars} onChange={(val) => selectEquipmentForRental(idx, val, 'Monthly')} options={deviceCatalog} cellInputStyle={s.cellInput} placeholder="Equipment name..." />
                            </td>
                            {SHOW_EQUIPMENT_COMPANY_COLUMN && (
                              <td style={s.td}>
                                <input
                                  style={s.cellInput}
                                  value={item.company || ''}
                                  onChange={(e) => updateItem(idx, 'company', e.target.value)}
                                  placeholder="—"
                                  title="Filled from the device's registered company — never printed on the invoice"
                                />
                              </td>
                            )}
                            <td style={s.td}><input type="date" style={s.cellInput} value={item.start_date} onChange={(e) => updateItem(idx, 'start_date', e.target.value)} /></td>
                            <td style={s.td}><input type="date" style={s.cellInput} value={item.end_date} onChange={(e) => updateItem(idx, 'end_date', e.target.value)} /></td>
                            <td style={s.td}>
                              <input
                                style={{ ...s.cellInput, textAlign: 'center' }}
                                value={item.hsn_code || ''}
                                onChange={(e) => updateItem(idx, 'hsn_code', e.target.value)}
                                placeholder="—"
                                title="Filled from the device's registered HSN — override here if this bill differs"
                              />
                            </td>
                            <td style={{ ...s.td, color: '#64748b', fontSize: '0.82rem', paddingLeft: '6px' }}>Monthly</td>
                            <td style={s.td}><input type="number" style={s.cellInput} value={item.rate} onChange={(e) => updateItem(idx, 'rate', e.target.value)} placeholder="0" /></td>
                            <td style={s.td}>
                              <select style={{ ...s.cellInput, cursor: 'pointer', backgroundColor: 'white' }} value={item.has_gst ? 'yes' : 'no'} onChange={(e) => updateItem(idx, 'has_gst', e.target.value === 'yes')}>
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                            </td>
                            <td style={s.td}>
                              {item.has_gst ? (
                                <select style={{ ...s.cellInput, cursor: 'pointer', backgroundColor: 'white' }} value={item.gst_percent} onChange={(e) => updateItem(idx, 'gst_percent', Number(e.target.value))}>
                                  <option value={5}>5%</option>
                                  <option value={12}>12%</option>
                                  <option value={18}>18%</option>
                                </select>
                              ) : <span style={{ color: '#cbd5e1', fontSize: '0.82rem', paddingLeft: '4px' }}>—</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              {item.has_gst ? <span style={{ fontWeight: 600, color: '#7e22ce' }}>₹{fmtAmt(item.gst_amount)}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}><span style={{ fontWeight: 600 }}>₹{fmtAmt(item.total_amount)}</span></td>
                            <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* ── Sale Equipment ────────────────────────────────── */}
                  {subHeader('Sale Equipment', '#7e22ce', '#e9d5ff')}
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, width: '3%' }}>#</th>
                        <th style={{ ...s.th, width: '22%' }}>Equipment Name</th>
                        {SHOW_EQUIPMENT_COMPANY_COLUMN && <th style={{ ...s.th, width: '12%' }}>Company</th>}
                        <th style={{ ...s.th, width: '8%', textAlign: 'center' }}>HSN</th>
                        <th style={{ ...s.th, width: '12%', textAlign: 'right' }}>Purchase Cost ₹</th>
                        <th style={{ ...s.th, width: '12%', textAlign: 'right' }}>Selling Price ₹</th>
                        <th style={{ ...s.th, width: '6%' }}>GST?</th>
                        <th style={{ ...s.th, width: '7%' }}>GST %</th>
                        <th style={{ ...s.th, width: '12%', textAlign: 'right' }}>GST Amt ₹</th>
                        <th style={{ ...s.th, width: '12%', textAlign: 'right' }}>Total ₹</th>
                        <th style={{ ...s.th, width: '4%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {saleRows.length === 0 ? renderNoItems(SHOW_EQUIPMENT_COMPANY_COLUMN ? 11 : 10) : saleRows.map(({ item, idx }) => {
                        counter++;
                        return (
                          <tr key={idx}>
                            <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{counter}</td>
                            <td style={s.td}>
                              <SearchDropdown value={item.particulars} onChange={(val) => selectEquipmentForSale(idx, val)} options={deviceCatalog} cellInputStyle={s.cellInput} placeholder="Equipment name..." />
                            </td>
                            {SHOW_EQUIPMENT_COMPANY_COLUMN && (
                              <td style={s.td}>
                                <input
                                  style={s.cellInput}
                                  value={item.company || ''}
                                  onChange={(e) => updateItem(idx, 'company', e.target.value)}
                                  placeholder="—"
                                  title="Filled from the device's registered company — never printed on the invoice"
                                />
                              </td>
                            )}
                            <td style={s.td}>
                              <input
                                style={{ ...s.cellInput, textAlign: 'center' }}
                                value={item.hsn_code || ''}
                                onChange={(e) => updateItem(idx, 'hsn_code', e.target.value)}
                                placeholder="—"
                                title="Filled from the device's registered HSN — override here if this bill differs"
                              />
                            </td>
                            <td style={s.td}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'right', backgroundColor: '#f8fafc', color: '#64748b' }} value={item.purchase_cost} readOnly disabled placeholder="—" />
                            </td>
                            <td style={s.td}><input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.rate} onChange={(e) => updateItem(idx, 'rate', e.target.value)} placeholder="0" /></td>
                            <td style={s.td}>
                              <select style={{ ...s.cellInput, cursor: 'pointer', backgroundColor: 'white' }} value={item.has_gst ? 'yes' : 'no'} onChange={(e) => updateItem(idx, 'has_gst', e.target.value === 'yes')}>
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                            </td>
                            <td style={s.td}>
                              {item.has_gst ? (
                                <select style={{ ...s.cellInput, cursor: 'pointer', backgroundColor: 'white' }} value={item.gst_percent} onChange={(e) => updateItem(idx, 'gst_percent', Number(e.target.value))}>
                                  <option value={5}>5%</option>
                                  <option value={12}>12%</option>
                                  <option value={18}>18%</option>
                                </select>
                              ) : <span style={{ color: '#cbd5e1', fontSize: '0.82rem', paddingLeft: '4px' }}>—</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              {item.has_gst ? <span style={{ fontWeight: 600, color: '#7e22ce' }}>₹{fmtAmt(item.gst_amount)}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}><span style={{ fontWeight: 600 }}>₹{fmtAmt(item.total_amount)}</span></td>
                            <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* ── Misc rows within this section ─────────────────── */}
                  {miscEquipRows.length > 0 && (
                    <>
                      {subHeader('Miscellaneous', '#92400e', '#fde68a')}
                      <table style={s.table}>
                        <tbody>
                          {miscEquipRows.map(({ item, idx }) => {
                            counter++;
                            return (
                              <tr key={idx} style={{ backgroundColor: '#fffbeb' }}>
                                <td style={{ ...s.td, width: '3%', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{counter}</td>
                                <td style={s.td} colSpan={10}>
                                  <input style={s.cellInput} value={item.particulars} onChange={(e) => updateItem(idx, 'particulars', e.target.value)} placeholder="Description" />
                                </td>
                                <td style={{ ...s.td, textAlign: 'right' }}>
                                  <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.total_amount || ''} onChange={(e) => updateItem(idx, 'total_amount', parseFloat(e.target.value) || 0)} placeholder="0" />
                                </td>
                                <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* ── Footer buttons ────────────────────────────────── */}
                  <div style={{ display: 'flex', gap: '6px', padding: '0.5rem 0', borderBottom: `2px solid ${sec.borderColor}`, marginBottom: '1rem' }}>
                    <button style={sectionAddBtn('violet')} onClick={addRentalRow}>+ Add Rental</button>
                    <button style={sectionAddBtn('purple')} onClick={addSaleRow}>+ Add Sale</button>
                  </div>
                </div>
              );
            }

            if (sec.key === 'equipment_rental') {
              // ── Equipment Rental (dedicated rental-only flow) ──────────────
              const rentalRows  = sectionItems.filter(({ item }) => !item.is_miscellaneous);
              const miscEquipRows = sectionItems.filter(({ item }) => item.is_miscellaneous);
              // CGST/SGST are each half the GST rate. The rate comes from the
              // device, so every row usually carries the same one and it can sit
              // in the header. When a bill mixes rates there is no single number
              // to show, so the header drops the bracket and the GST % column
              // still gives it per row.
              const rentalRates = Array.from(new Set(
                rentalRows.map(({ item }) => Number(item.gst_percent) || 0).filter(p => p > 0)
              ));
              const rentalHalfRate = rentalRates.length === 1 ? rentalRates[0] / 2 : null;
              const halfLabel = (base: string) =>
                rentalHalfRate !== null ? `${base} (${+rentalHalfRate.toFixed(2)}%) ₹` : `${base} ₹`;
              const subHeader = (label: string, color: string, borderColor: string) => (
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase' as const, letterSpacing: '0.05em', padding: '0.35rem 0', borderBottom: `1px solid ${borderColor}`, marginBottom: '0.2rem', marginTop: '0.6rem' }}>{label}</div>
              );
              return (
                <div key={sec.key}>
                  {renderSectionHeader(sec)}

                  {/* ── Medical Equipment Rental Services table ──────── */}
                  {subHeader('Medical Equipment Rental Services', '#7e22ce', '#e9d5ff')}
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, width: '3%' }}>#</th>
                        <th style={{ ...s.th, width: '20%' }}>Equipment Name</th>
                        {SHOW_EQUIPMENT_COMPANY_COLUMN && <th style={{ ...s.th, width: '12%' }}>Company</th>}
                        <th style={{ ...s.th, width: '9%' }}>Start Date</th>
                        <th style={{ ...s.th, width: '9%' }}>End Date</th>
                        <th style={{ ...s.th, width: '8%', textAlign: 'center' }}>HSN</th>
                        <th style={{ ...s.th, width: '11%', textAlign: 'right' }}>Amount ₹</th>
                        <th style={{ ...s.th, width: '6%', textAlign: 'center' }}>GST %</th>
                        <th style={{ ...s.th, width: '10%', textAlign: 'right' }}>{halfLabel('CGST')}</th>
                        <th style={{ ...s.th, width: '10%', textAlign: 'right' }}>{halfLabel('SGST')}</th>
                        <th style={{ ...s.th, width: '10%', textAlign: 'right' }}>Total ₹</th>
                        <th style={{ ...s.th, width: '3%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rentalRows.length === 0 ? renderNoItems(SHOW_EQUIPMENT_COMPANY_COLUMN ? 12 : 11) : rentalRows.map(({ item, idx }) => {
                        counter++;
                        // GST now follows the device's registered rate rather than a
                        // Yes/No the biller sets by hand: a rate of 0 (a Non-GST
                        // device, or one with no GST recorded) simply has no GST.
                        const gstOff = !(Number(item.gst_percent) > 0);
                        return (
                          <tr key={idx}>
                            <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{counter}</td>
                            <td style={s.td}>
                              <SearchDropdown value={item.particulars} onChange={(val) => selectEquipmentForRental(idx, val, 'Monthly')} options={deviceCatalog} cellInputStyle={s.cellInput} placeholder="Equipment name..." />
                            </td>
                            {SHOW_EQUIPMENT_COMPANY_COLUMN && (
                              <td style={s.td}>
                                <input
                                  style={s.cellInput}
                                  value={item.company || ''}
                                  onChange={(e) => updateItem(idx, 'company', e.target.value)}
                                  placeholder="—"
                                  title="Filled from the device's registered company — never printed on the invoice"
                                />
                              </td>
                            )}
                            <td style={s.td}><input type="date" style={s.cellInput} value={item.start_date} onChange={(e) => updateItem(idx, 'start_date', e.target.value)} /></td>
                            <td style={s.td}><input type="date" style={s.cellInput} value={item.end_date} onChange={(e) => updateItem(idx, 'end_date', e.target.value)} /></td>
                            <td style={s.td}>
                              <input
                                style={{ ...s.cellInput, textAlign: 'center' }}
                                value={item.hsn_code || ''}
                                onChange={(e) => updateItem(idx, 'hsn_code', e.target.value)}
                                placeholder="—"
                                title="Filled from the device's registered HSN — override here if this bill differs"
                              />
                            </td>
                            <td style={s.td}><input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.rate} onChange={(e) => updateItem(idx, 'rate', e.target.value)} placeholder="0" /></td>
                            <td style={s.td}>
                              <input
                                type="number"
                                style={{ ...s.cellInput, textAlign: 'center' }}
                                value={item.gst_percent || ''}
                                onChange={(e) => updateItem(idx, 'gst_percent', e.target.value)}
                                placeholder="0"
                                title="Filled from the device's registered GST — override here if this bill differs"
                              />
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              {gstOff ? <span style={{ color: '#cbd5e1' }}>—</span> : <span style={{ fontWeight: 600, color: '#7e22ce' }}>₹{fmtAmt(item.cgst_amount)}</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              {gstOff ? <span style={{ color: '#cbd5e1' }}>—</span> : <span style={{ fontWeight: 600, color: '#7e22ce' }}>₹{fmtAmt(item.sgst_amount)}</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}><span style={{ fontWeight: 600 }}>₹{fmtAmt(item.total_amount)}</span></td>
                            <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* ── Misc rows ─────────────────────────────────────── */}
                  {miscEquipRows.length > 0 && (
                    <>
                      {subHeader('Miscellaneous', '#92400e', '#fde68a')}
                      <table style={s.table}>
                        <tbody>
                          {miscEquipRows.map(({ item, idx }) => {
                            counter++;
                            return (
                              <tr key={idx} style={{ backgroundColor: '#fffbeb' }}>
                                <td style={{ ...s.td, width: '3%', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{counter}</td>
                                <td style={s.td} colSpan={9}>
                                  <input style={s.cellInput} value={item.particulars} onChange={(e) => updateItem(idx, 'particulars', e.target.value)} placeholder="Description" />
                                </td>
                                <td style={{ ...s.td, textAlign: 'right' }}>
                                  <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.total_amount || ''} onChange={(e) => updateItem(idx, 'total_amount', parseFloat(e.target.value) || 0)} placeholder="0" />
                                </td>
                                <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* ── Footer ────────────────────────────────────────── */}
                  <div style={{ display: 'flex', gap: '6px', padding: '0.5rem 0', borderBottom: `2px solid ${sec.borderColor}`, marginBottom: '1rem' }}>
                    <button style={sectionAddBtn('violet')} onClick={addRentalRow}>+ Add Rental</button>
                    <button style={sectionAddBtn('green')} onClick={() => addMiscToSection('equipment_rental')}>+ Add Misc</button>
                  </div>
                </div>
              );
            }

            if (sec.key === 'equipment_sale') {
              // ── Equipment Sale — retail math (MRP / discount / qty / CGST / SGST) ──
              const saleRows      = sectionItems.filter(({ item }) => !item.is_miscellaneous);
              const miscEquipRows = sectionItems.filter(({ item }) => item.is_miscellaneous);
              const subHeader = (label: string, color: string, borderColor: string) => (
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase' as const, letterSpacing: '0.05em', padding: '0.35rem 0', borderBottom: `1px solid ${borderColor}`, marginBottom: '0.2rem', marginTop: '0.6rem' }}>{label}</div>
              );
              return (
                <div key={sec.key}>
                  {renderSectionHeader(sec)}

                  {/* ── Sale Equipment table ──────────────────────────── */}
                  {subHeader('Sale Equipment', '#4338ca', '#c7d2fe')}
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, width: '3%' }}>#</th>
                        <th style={{ ...s.th, width: '15%' }}>Equipment Name</th>
                        {SHOW_EQUIPMENT_COMPANY_COLUMN && <th style={{ ...s.th, width: '10%' }}>Company</th>}
                        <th style={{ ...s.th, width: '7%', textAlign: 'center' }}>HSN</th>
                        <th style={{ ...s.th, width: '7%', textAlign: 'right' }}>Purchase Cost ₹</th>
                        <th style={{ ...s.th, width: '7%', textAlign: 'right' }}>MRP ₹</th>
                        <th style={{ ...s.th, width: '5%', textAlign: 'right' }}>Disc. %</th>
                        <th style={{ ...s.th, width: '6%', textAlign: 'right' }}>Disc. Price ₹</th>
                        <th style={{ ...s.th, width: '4%', textAlign: 'center' }}>Qty</th>
                        <th style={{ ...s.th, width: '5%', textAlign: 'center' }}>GST?</th>
                        <th style={{ ...s.th, width: '5%', textAlign: 'center' }}>GST %</th>
                        <th style={{ ...s.th, width: '6%', textAlign: 'right' }}>CGST ₹</th>
                        <th style={{ ...s.th, width: '6%', textAlign: 'right' }}>SGST ₹</th>
                        <th style={{ ...s.th, width: '8%', textAlign: 'right' }}>Total ₹</th>
                        <th style={{ ...s.th, width: '4%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {saleRows.length === 0 ? renderNoItems(SHOW_EQUIPMENT_COMPANY_COLUMN ? 15 : 14) : saleRows.map(({ item, idx }) => {
                        counter++;
                        const gstOff = !item.has_gst;
                        return (
                          <tr key={idx}>
                            <td style={{ ...s.td, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{counter}</td>
                            <td style={s.td}>
                              <SearchDropdown value={item.particulars} onChange={(val) => selectEquipmentForSale(idx, val)} options={deviceCatalog} cellInputStyle={s.cellInput} placeholder="Equipment name..." />
                            </td>
                            {SHOW_EQUIPMENT_COMPANY_COLUMN && (
                              <td style={s.td}>
                                <input
                                  style={s.cellInput}
                                  value={item.company || ''}
                                  onChange={(e) => updateItem(idx, 'company', e.target.value)}
                                  placeholder="—"
                                  title="Filled from the device's registered company — never printed on the invoice"
                                />
                              </td>
                            )}
                            <td style={s.td}>
                              <input
                                style={{ ...s.cellInput, textAlign: 'center' }}
                                value={item.hsn_code || ''}
                                onChange={(e) => updateItem(idx, 'hsn_code', e.target.value)}
                                placeholder="—"
                                title="Filled from the device's registered HSN — override here if this bill differs"
                              />
                            </td>
                            <td style={s.td}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'right', backgroundColor: '#f8fafc', color: '#64748b' }} value={item.purchase_cost} readOnly disabled placeholder="—" />
                            </td>
                            <td style={s.td}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.mrp} onChange={(e) => updateItem(idx, 'mrp', e.target.value)} placeholder="0" />
                            </td>
                            <td style={s.td}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.discount_percent} onChange={(e) => updateItem(idx, 'discount_percent', e.target.value)} placeholder="0" />
                            </td>
                            <td style={s.td}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.disc_price} onChange={(e) => updateItem(idx, 'disc_price', e.target.value)} placeholder="0" />
                            </td>
                            <td style={s.td}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'center' }} value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} placeholder="1" />
                            </td>
                            <td style={s.td}>
                              <select style={{ ...s.cellInput, cursor: 'pointer', backgroundColor: 'white' }} value={item.has_gst ? 'yes' : 'no'} onChange={(e) => updateItem(idx, 'has_gst', e.target.value === 'yes')}>
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                            </td>
                            <td style={s.td}>
                              {gstOff
                                ? <span style={{ color: '#cbd5e1', fontSize: '0.82rem', paddingLeft: '4px' }}>—</span>
                                : <input type="number" style={{ ...s.cellInput, textAlign: 'center' }} value={item.gst_percent} onChange={(e) => updateItem(idx, 'gst_percent', e.target.value)} placeholder="0" />}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              {gstOff
                                ? <span style={{ color: '#cbd5e1' }}>—</span>
                                : <span style={{ fontWeight: 600, color: '#4338ca' }}>₹{fmtAmt(item.cgst_amount)}</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              {gstOff
                                ? <span style={{ color: '#cbd5e1' }}>—</span>
                                : <span style={{ fontWeight: 600, color: '#4338ca' }}>₹{fmtAmt(item.sgst_amount)}</span>}
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}><span style={{ fontWeight: 600 }}>₹{fmtAmt(item.total_amount)}</span></td>
                            <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* ── Misc rows ─────────────────────────────────────── */}
                  {miscEquipRows.length > 0 && (
                    <>
                      {subHeader('Miscellaneous', '#92400e', '#fde68a')}
                      <table style={s.table}>
                        <tbody>
                          {miscEquipRows.map(({ item, idx }) => {
                            counter++;
                            return (
                              <tr key={idx} style={{ backgroundColor: '#fffbeb' }}>
                                <td style={{ ...s.td, width: '3%', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>{counter}</td>
                                <td style={s.td} colSpan={11}>
                                  <input style={s.cellInput} value={item.particulars} onChange={(e) => updateItem(idx, 'particulars', e.target.value)} placeholder="Description" />
                                </td>
                                <td style={{ ...s.td, textAlign: 'right' }}>
                                  <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.total_amount || ''} onChange={(e) => updateItem(idx, 'total_amount', parseFloat(e.target.value) || 0)} placeholder="0" />
                                </td>
                                <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* ── Footer ────────────────────────────────────────── */}
                  <div style={{ display: 'flex', gap: '6px', padding: '0.5rem 0', borderBottom: `2px solid ${sec.borderColor}`, marginBottom: '1rem' }}>
                    <button style={sectionAddBtn('indigo')} onClick={addSaleRow}>+ Add Sale</button>
                    <button style={sectionAddBtn('green')} onClick={() => addMiscToSection('equipment_sale')}>+ Add Misc</button>
                  </div>
                </div>
              );
            }

            if (sec.key === 'misc') {
              // ── Miscellaneous ──────────────────────────────────────────
              return (
                <div key={sec.key}>
                  {renderSectionHeader(sec)}
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, width: '4%' }}>#</th>
                        <th style={{ ...s.th, width: '44%' }}>Description</th>
                        <th style={{ ...s.th, width: '14%' }}>Start Date</th>
                        <th style={{ ...s.th, width: '14%' }}>End Date</th>
                        <th style={{ ...s.th, width: '18%', textAlign: 'right' }}>Amount (₹)</th>
                        <th style={{ ...s.th, width: '6%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionItems.length === 0 ? renderNoItems(6) : sectionItems.map(({ item, idx }) => {
                        counter++;
                        return (
                          <tr key={idx} style={{ backgroundColor: '#fffbeb' }}>
                            <td style={{ ...s.td, color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>{counter}</td>
                            <td style={s.td}>
                              <input style={s.cellInput} value={item.particulars} onChange={(e) => updateItem(idx, 'particulars', e.target.value)} placeholder="Description of item" />
                            </td>
                            <td style={s.td}>
                              <input type="date" style={s.cellInput} value={item.start_date} onChange={(e) => updateItem(idx, 'start_date', e.target.value)} />
                            </td>
                            <td style={s.td}>
                              <input type="date" style={s.cellInput} value={item.end_date} onChange={(e) => updateItem(idx, 'end_date', e.target.value)} />
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.total_amount || ''}
                                onChange={(e) => updateItem(idx, 'total_amount', parseFloat(e.target.value) || 0)} placeholder="0" />
                            </td>
                            <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {renderSectionFooter(sec)}
                </div>
              );
            }

            // ── Nursing / Nursing Visit / Doctor Visit / Physio ──────────
            const isDoctorVisit = sec.key === 'doctor_visit';
            const isNursingVisit = sec.key === 'nursing_visit';
            const isVisitType = isNursingVisit || sec.key === 'physio';
            const isRateType = sec.key === 'nursing' || isNursingVisit || sec.key === 'physio';
            const shiftOpts = isVisitType ? VISIT_OPTIONS : SHIFT_OPTIONS;
            return (
              <div key={sec.key}>
                {renderSectionHeader(sec)}
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width: '4%' }}>#</th>
                      <th style={{ ...s.th, width: '16%' }}>Particulars</th>
                      {isDoctorVisit && <th style={{ ...s.th, width: '18%' }}>Doctor Name</th>}
                      <th style={{ ...s.th, width: '12%' }}>Start Date</th>
                      <th style={{ ...s.th, width: '12%' }}>End Date</th>
                      {!isDoctorVisit && <th style={{ ...s.th, width: '18%' }}>{isVisitType ? 'Visit Type' : 'Shift Type'}</th>}
                      <th style={{ ...s.th, width: '10%' }}>{isRateType ? 'Rate (₹)' : 'Amount (₹)'}</th>
                      {!isDoctorVisit && <th style={{ ...s.th, width: '8%' }}>{isVisitType ? 'Visits' : 'Shifts'}</th>}
                      <th style={{ ...s.th, width: isDoctorVisit ? '22%' : '16%', textAlign: 'right' }}>Total (₹)</th>
                      <th style={{ ...s.th, width: '6%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionItems.length === 0 ? renderNoItems(isDoctorVisit ? 7 : 9) : sectionItems.map(({ item, idx }) => {
                      counter++;
                      if (item.is_miscellaneous) {
                        return (
                          <tr key={idx} style={{ backgroundColor: '#fffbeb' }}>
                            <td style={{ ...s.td, color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>{counter}</td>
                            <td style={s.td} colSpan={isDoctorVisit ? 3 : 4}>
                              <input style={s.cellInput} value={item.particulars} onChange={(e) => updateItem(idx, 'particulars', e.target.value)} placeholder="Description" />
                            </td>
                            <td style={s.td}>
                              <input type="date" style={s.cellInput} value={item.start_date} onChange={(e) => updateItem(idx, 'start_date', e.target.value)} />
                            </td>
                            <td style={s.td}>
                              <input type="date" style={s.cellInput} value={item.end_date} onChange={(e) => updateItem(idx, 'end_date', e.target.value)} />
                            </td>
                            <td style={{ ...s.td, textAlign: 'right' }}>
                              <input type="number" style={{ ...s.cellInput, textAlign: 'right' }} value={item.total_amount || ''}
                                onChange={(e) => updateItem(idx, 'total_amount', parseFloat(e.target.value) || 0)} placeholder="0" />
                            </td>
                            <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={idx}>
                          <td style={{ ...s.td, color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>{counter}</td>
                          <td style={s.td}>
                            <input style={s.cellInput} value={item.particulars} onChange={(e) => updateItem(idx, 'particulars', e.target.value)} placeholder={item.particulars || 'Description'} />
                          </td>
                          {isDoctorVisit && (
                            <td style={s.td}>
                              <input style={s.cellInput} value={item.shift_type} onChange={(e) => updateItem(idx, 'shift_type', e.target.value)} placeholder="Doctor name" />
                            </td>
                          )}
                          <td style={s.td}>
                            <input type="date" style={s.cellInput} value={item.start_date} onChange={(e) => updateItem(idx, 'start_date', e.target.value)} />
                          </td>
                          <td style={s.td}>
                            <input type="date" style={s.cellInput} value={item.end_date} onChange={(e) => updateItem(idx, 'end_date', e.target.value)} />
                          </td>
                          {!isDoctorVisit && (
                            <td style={s.td}>
                              <select style={s.cellInput} value={item.shift_type} onChange={(e) => updateItem(idx, 'shift_type', e.target.value)}>
                                <option value="">— Select —</option>
                                {item.shift_type && !shiftOpts.includes(item.shift_type) && (
                                  <option value={item.shift_type}>{item.shift_type}</option>
                                )}
                                {shiftOpts.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </td>
                          )}
                          <td style={s.td}>
                            <input type="number" style={s.cellInput} value={item.rate} onChange={(e) => updateItem(idx, 'rate', e.target.value)} placeholder="0" />
                          </td>
                          {!isDoctorVisit && (
                            <td style={s.td}>
                              <input type="number" style={s.cellInput} value={item.shifts_count} onChange={(e) => updateItem(idx, 'shifts_count', e.target.value)} placeholder="0" />
                            </td>
                          )}
                          <td style={{ ...s.td, textAlign: 'right' }}>
                            <span style={{ fontWeight: 600 }}>{fmtAmt(item.total_amount)}</span>
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' }}><button style={s.deleteBtn} onClick={() => removeItem(idx)}>✕</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {renderSectionFooter(sec)}
              </div>
            );
          });
        })()}

        {/* Summary */}
        {lineItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <div style={{ ...s.summaryBox, minWidth: '300px' }}>
              {visibleSections.map((section) => {
                const total = lineItems.filter((i) => i.group === section.key).reduce((sum, i) => sum + i.total_amount, 0);
                if (total === 0) return null;
                return (
                  <div key={section.key} style={s.summaryLine}>
                    <span>{section.label}</span>
                    <span>₹{fmtAmt(total)}</span>
                  </div>
                );
              })}
              {/* Discount sits between the section subtotals and the payable
                  total, the way it reads on the printed invoice. The
                  description is optional; the amount is what makes it appear. */}
              <div style={{ ...s.summaryLine, alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  value={discountDescription}
                  onChange={(e) => setDiscountDescription(e.target.value)}
                  placeholder="Discount (reason)"
                  style={{
                    flex: 1, minWidth: 0, padding: '4px 8px', borderRadius: '6px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#334155',
                    outline: 'none', fontFamily: 'inherit',
                  }}
                />
                <input
                  type="number"
                  min={0}
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="0"
                  style={{
                    width: '110px', flexShrink: 0, padding: '4px 8px', borderRadius: '6px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#334155',
                    outline: 'none', textAlign: 'right', fontFamily: 'inherit',
                  }}
                />
              </div>
              {discountValue > 0 && (
                <div style={{ ...s.summaryLine, color: '#dc2626' }}>
                  <span>{discountDescription.trim() || 'Discount'}</span>
                  <span>− ₹{fmtAmt(discountValue)}</span>
                </div>
              )}
              <div style={s.grandTotalLine}>
                <span>Net Total Payable</span>
                <span>₹{fmtAmt(grandTotal)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Remarks */}
      <div style={s.card}>
        <p style={s.sectionTitle}>Remarks</p>
        <textarea style={s.textarea} value={remarks} onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any additional notes or remarks for this invoice..." />
      </div>

      {/* Bottom action bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button style={btn('outline')} onClick={() => setShowPreview(true)}>Preview Invoice</button>
        <button style={btn('primary')} onClick={handleSave} disabled={saving || createdBillId !== null}>
          {saving ? 'Saving...' : isCreateMode ? 'Create Bill' : 'Save Changes'}
        </button>
      </div>

      {/* PDF Preview Modal — in create mode this is a draft: no download until saved */}
      {showPreview && patient && (
        <InvoicePreviewModal
          draft={isCreateMode}
          createLabel="Create Bill & Download"
          onCreate={handleSave}
          creating={saving}
          onDownload={handleDownloadPDF}
          downloading={downloadingPDF}
          onClose={() => setShowPreview(false)}
        >
          <BillInvoicePreview
            ref={invoiceRef}
            patient={patient}
            billNo={typeof billNo === 'number' ? billNo : 0}
            invoiceNo={invoiceNo}
            billDate={billDate}
            periodStart={periodStart}
            periodEnd={periodEnd}
            lineItems={lineItems}
            remarks={remarks}
            discountDescription={discountDescription}
            discountAmount={discountValue}
            billType={derivedPrimary}
            hasGst={lineItems.some((i) => i.has_gst)}
            createdAt={new Date().toISOString()}
          />
        </InvoicePreviewModal>
      )}

      {/* After creating, open the bill preview so it can be printed/downloaded directly */}
      {createdBillId !== null && patient && (
        <BillViewModal
          billId={createdBillId}
          patient={patient as any}
          onClose={() => { const id = createdBillId; setCreatedBillId(null); navigate(`/billing/bill/${id}`); }}
        />
      )}
    </div>
  );
};

export default BillingSheet;
